/**
 * SearchPalette — the global ⌘F workspace search as a workbench primitive.
 *
 * Same modal, same index, same hotkey as the viewer's search (shared via
 * `useSearchModal`/`SearchModal`); only the pick ROUTING differs — here a
 * hit dispatches through the store (selection, `boardFly`, nav reveal,
 * editor reveal via the source map) instead of the viewer's mode-switching
 * handlers.
 *
 * Owns ⌘F for its composition: pass `hotkey={false}` to the WorkspaceNav
 * mounted next to it, or both will fight over the key.
 *
 * Type entries are filtered out — the workbench has no types surface
 * (yet), and a pick that lands nowhere is worse than no result.
 */
import { useCallback, useMemo, type ReactElement } from "react";
import { SearchModal } from "../../components/SearchModal.js";
import type { SearchEntry } from "../../search/searchIndex.js";
import { useSearchModal, type SearchModalState } from "../../search/useSearchModal.js";
import { useActiveView } from "../ActiveViewContext.js";
import { useWorkspace } from "../WorkspaceContext.js";
import { useWorkspaceModel } from "../WorkspaceModelContext.js";
import { routeSearchPick } from "./routeSearchPick.js";

export interface SearchPaletteProps {
  /**
   * Externally-owned search state (a `useSearchModal` result) — lets the
   * host add open triggers beyond ⌘F (the viewer's phone header has a
   * search button). Omit and the palette runs its own.
   */
  readonly state?: SearchModalState;
  /**
   * Full replacement for the default store routing — the viewer routes
   * picks through its mode-switching handlers (types/docs surfaces the
   * workbench doesn't have). With a custom router, NO kinds are filtered;
   * the router owns every entry.
   */
  readonly route?: (entry: SearchEntry) => void;
}

const EMPTY_SEARCH_PALETTE_PROPS: SearchPaletteProps = {};

export function SearchPalette(props: SearchPaletteProps = EMPTY_SEARCH_PALETTE_PROPS): ReactElement {
  const store = useWorkspace();
  const { model } = useWorkspaceModel();
  const own = useSearchModal(props.state ? null : model, !props.state);
  const { searchOpen, setSearchOpen, entries } = props.state ?? own;

  // Default routing can't land a "type" pick anywhere — filter those out.
  // A custom router owns every kind (the viewer has a types surface).
  const routable = useMemo(
    () => (props.route ? entries : entries.filter((e) => e.target.kind !== "type")),
    [entries, props.route],
  );

  const { route } = props;
  // Plane picks route to the ONE plane store (ActiveViewContext) — the store
  // router can't reach it, and a pick that lands nowhere is worse than no
  // result (r48).
  const { setActivePlane } = useActiveView();
  const onPick = useCallback(
    (entry: SearchEntry) => {
      if (route) return route(entry);
      if (entry.target.kind === "plane") {
        setActivePlane(entry.target.plane);
        return;
      }
      routeSearchPick(store, entry);
    },
    [route, store, setActivePlane],
  );

  return (
    <SearchModal
      open={searchOpen}
      entries={routable}
      onClose={() => setSearchOpen(false)}
      onPick={onPick}
    />
  );
}
